if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          indicators: this.querySelector('[id^="MediaIndicators"]'),
          arrows: this.querySelector('[id^="MediaArrows"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Verificar se viewer existe antes de continuar
        if (!this.elements.viewer) {
          return;
        }

        // Listener único para slideChanged que atualiza tudo
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', (event) => {
            const currentElement = event?.detail?.currentElement;
            if (currentElement?.dataset?.mediaId) {
              this.setActiveMedia(currentElement.dataset.mediaId, false);
            }
          });
        }
        
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        
        // Adicionar listeners para indicadores
        if (this.elements.indicators) {
          this.setupIndicatorListeners();
          // Re-setup quando indicadores forem atualizados
          const observer = new MutationObserver(() => {
            this.setupIndicatorListeners();
          });
          observer.observe(this.elements.indicators, { childList: true, subtree: true });
        }
        
        // Adicionar listeners para arrows laterais (desktop)
        if (this.elements.arrows) {
          const prevArrow = this.elements.arrows.querySelector('.product__media-arrow--prev');
          const nextArrow = this.elements.arrows.querySelector('.product__media-arrow--next');
          
          if (prevArrow) {
            prevArrow.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.navigateMedia('prev');
            });
          }
          
          if (nextArrow) {
            nextArrow.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.navigateMedia('next');
            });
          }
        }
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
        
        this.updateIndicators();
      }

      onSlideChanged(event) {
        if (!event || !event.detail || !event.detail.currentElement) return;
        
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          if (thumbnail) {
            this.setActiveThumbnail(thumbnail);
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;
        
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        
        // Remover is-active de todos os elementos
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Adicionar is-active ao elemento correto
        activeMedia.classList.add('is-active');
        
        // Garantir que o elemento ativo seja visível
        if (activeMedia.style.display === 'none') {
          activeMedia.style.display = '';
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
        
        // Atualizar indicadores e ações
        // Usar setTimeout para garantir que o DOM foi atualizado
        setTimeout(() => {
          this.updateIndicators();
        }, 50);
      }

      setupIndicatorListeners() {
        if (!this.elements.indicators) return;
        
        this.elements.indicators.querySelectorAll('.product__media-indicator').forEach((indicator) => {
          if (indicator.hasAttribute('data-listener-added')) return;
          
          indicator.setAttribute('data-listener-added', 'true');
          indicator.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const mediaId = indicator.dataset.mediaId;
            if (mediaId) {
              this.setActiveMedia(mediaId, false);
            }
          });
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer) return;
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }

      updateIndicators() {
        if (!this.elements.indicators) return;
        if (!this.elements.viewer) return;
        
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (!activeMedia) return;
        
        const activeMediaId = activeMedia.dataset.mediaId;
        const indicators = this.elements.indicators.querySelectorAll('.product__media-indicator');
        
        indicators.forEach((indicator) => {
          if (indicator.dataset.mediaId === activeMediaId) {
            indicator.classList.add('is-active');
          } else {
            indicator.classList.remove('is-active');
          }
          
          // Garantir que indicadores sejam clicáveis
          if (!indicator.hasAttribute('data-listener-added')) {
            indicator.setAttribute('data-listener-added', 'true');
            indicator.addEventListener('click', () => {
              const mediaId = indicator.dataset.mediaId;
              if (mediaId) {
                this.setActiveMedia(mediaId, false);
              }
            });
          }
        });
      }


      navigateMedia(direction) {
        // Sempre usar setActiveMedia para navegação, que funciona tanto com slider quanto sem
        const allMediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        const activeIndex = allMediaItems.findIndex(item => item.classList.contains('is-active'));
        
        if (activeIndex === -1) return;
        
        let nextIndex;
        if (direction === 'next') {
          nextIndex = (activeIndex + 1) % allMediaItems.length;
        } else {
          nextIndex = (activeIndex - 1 + allMediaItems.length) % allMediaItems.length;
        }
        
        const nextMedia = allMediaItems[nextIndex];
        const mediaId = nextMedia.dataset.mediaId;
        if (mediaId) {
          this.setActiveMedia(mediaId, false);
        }
      }
    }
  );
}
